/**
 * Custom Forms — clinic-authored outbound documents (referrals, consents, ABNs,
 * records transfers, good faith estimates, etc.). Sibling of Custom Surveys in
 * lifecycle, but a positioned-document shape that renders to a PDF.
 *
 * This file is the shared contract threaded across the call chain:
 * builder -> service -> token resolver -> PDF renderer. No inline shapes.
 */

export type FormCategory = 'clinical' | 'financial' | 'consent' | 'legal' | 'records';

export type FormRenderMode = 'scratch' | 'pdf-overlay';

export const FORM_CATEGORIES = [
  { value: 'clinical', label: 'Clinical' },
  { value: 'financial', label: 'Financial' },
  { value: 'consent', label: 'Consent' },
  { value: 'legal', label: 'Legal' },
  { value: 'records', label: 'Records' },
] as const;

/**
 * Destination folder: `destinationFolderId` is the id of a clinic global folder,
 * or `null` to mean the system-default "Forms" folder (resolved at storage time —
 * no per-clinic row is seeded). The UI select uses this sentinel value to represent
 * the null/system-default choice.
 */
export const FORM_SYSTEM_DEFAULT_FOLDER_VALUE = 'system-default';

/* ------------------------------------------------------------------ *
 * Token library
 * ------------------------------------------------------------------ */

export type FormTokenSource =
  | 'patient'
  | 'insurancePrimary'
  | 'insuranceSecondary'
  | 'insuranceTertiary'
  | 'diagnosis'
  | 'complaint'
  | 'provider'
  | 'clinic'
  | 'healthcareProvider'
  | 'healthcareFacility';

export interface FormToken {
  id: string;
  source: FormTokenSource;
  label: string;
}

/** Display grouping + ordering for the builder sidebar. */
export const FORM_TOKEN_GROUPS: { source: FormTokenSource; label: string }[] = [
  { source: 'patient', label: 'Patient Demographics' },
  { source: 'insurancePrimary', label: 'Primary Insurance' },
  { source: 'insuranceSecondary', label: 'Secondary Insurance' },
  { source: 'insuranceTertiary', label: 'Tertiary Insurance' },
  { source: 'diagnosis', label: 'Diagnosis' },
  { source: 'complaint', label: 'Complaint' },
  { source: 'provider', label: 'Provider' },
  { source: 'clinic', label: 'Clinic Info' },
  { source: 'healthcareProvider', label: 'Healthcare Provider' },
  { source: 'healthcareFacility', label: 'Healthcare Facility' },
];

export const FORM_TOKENS = [
  // Patient Demographics
  { id: 'patient.prefix', source: 'patient', label: 'Prefix' },
  { id: 'patient.legalFirstName', source: 'patient', label: 'Legal First Name' },
  { id: 'patient.legalLastName', source: 'patient', label: 'Legal Last Name' },
  { id: 'patient.suffix', source: 'patient', label: 'Suffix' },
  { id: 'patient.preferredName', source: 'patient', label: 'Preferred Name' },
  { id: 'patient.dateOfBirth', source: 'patient', label: 'Date of Birth' },
  { id: 'patient.sex', source: 'patient', label: 'Sex' },
  { id: 'patient.raceEthnicity', source: 'patient', label: 'Race & Ethnicity' },
  { id: 'patient.address1', source: 'patient', label: 'Address 1' },
  { id: 'patient.address2', source: 'patient', label: 'Address cont.' },
  { id: 'patient.city', source: 'patient', label: 'City' },
  { id: 'patient.state', source: 'patient', label: 'State' },
  { id: 'patient.zip', source: 'patient', label: 'Zip' },
  { id: 'patient.mobilePhone', source: 'patient', label: 'Mobile Phone' },
  { id: 'patient.email', source: 'patient', label: 'Email' },

  // Primary Insurance
  { id: 'insurancePrimary.insuranceType', source: 'insurancePrimary', label: 'Insurance Type' },
  { id: 'insurancePrimary.payor', source: 'insurancePrimary', label: 'Payor' },
  {
    id: 'insurancePrimary.planName',
    source: 'insurancePrimary',
    label: 'Insurance Plan/Program Name',
  },
  { id: 'insurancePrimary.memberId', source: 'insurancePrimary', label: 'Member ID Number' },
  { id: 'insurancePrimary.groupId', source: 'insurancePrimary', label: 'Plan ID or Group ID' },
  { id: 'insurancePrimary.primaryInsured', source: 'insurancePrimary', label: 'Primary Insured' },
  {
    id: 'insurancePrimary.primaryInsuredFirstName',
    source: 'insurancePrimary',
    label: 'Primary Insured First Name',
  },
  {
    id: 'insurancePrimary.primaryInsuredLastName',
    source: 'insurancePrimary',
    label: 'Primary Insured Last Name',
  },
  {
    id: 'insurancePrimary.primaryInsuredDob',
    source: 'insurancePrimary',
    label: 'Primary Insured Date of Birth',
  },
  {
    id: 'insurancePrimary.primaryInsuredSex',
    source: 'insurancePrimary',
    label: 'Primary Insured Sex',
  },

  // Secondary Insurance
  { id: 'insuranceSecondary.insuranceType', source: 'insuranceSecondary', label: 'Insurance Type' },
  { id: 'insuranceSecondary.payor', source: 'insuranceSecondary', label: 'Payor' },
  {
    id: 'insuranceSecondary.planName',
    source: 'insuranceSecondary',
    label: 'Insurance Plan/Program Name',
  },
  { id: 'insuranceSecondary.memberId', source: 'insuranceSecondary', label: 'Member ID Number' },
  { id: 'insuranceSecondary.groupId', source: 'insuranceSecondary', label: 'Plan ID or Group ID' },
  {
    id: 'insuranceSecondary.primaryInsured',
    source: 'insuranceSecondary',
    label: 'Primary Insured',
  },
  {
    id: 'insuranceSecondary.primaryInsuredFirstName',
    source: 'insuranceSecondary',
    label: 'Primary Insured First Name',
  },
  {
    id: 'insuranceSecondary.primaryInsuredLastName',
    source: 'insuranceSecondary',
    label: 'Primary Insured Last Name',
  },
  {
    id: 'insuranceSecondary.primaryInsuredDob',
    source: 'insuranceSecondary',
    label: 'Primary Insured Date of Birth',
  },
  {
    id: 'insuranceSecondary.primaryInsuredSex',
    source: 'insuranceSecondary',
    label: 'Primary Insured Sex',
  },

  // Tertiary Insurance
  { id: 'insuranceTertiary.insuranceType', source: 'insuranceTertiary', label: 'Insurance Type' },
  { id: 'insuranceTertiary.payor', source: 'insuranceTertiary', label: 'Payor' },
  {
    id: 'insuranceTertiary.planName',
    source: 'insuranceTertiary',
    label: 'Insurance Plan/Program Name',
  },
  { id: 'insuranceTertiary.memberId', source: 'insuranceTertiary', label: 'Member ID Number' },
  { id: 'insuranceTertiary.groupId', source: 'insuranceTertiary', label: 'Plan ID or Group ID' },
  { id: 'insuranceTertiary.primaryInsured', source: 'insuranceTertiary', label: 'Primary Insured' },
  {
    id: 'insuranceTertiary.primaryInsuredFirstName',
    source: 'insuranceTertiary',
    label: 'Primary Insured First Name',
  },
  {
    id: 'insuranceTertiary.primaryInsuredLastName',
    source: 'insuranceTertiary',
    label: 'Primary Insured Last Name',
  },
  {
    id: 'insuranceTertiary.primaryInsuredDob',
    source: 'insuranceTertiary',
    label: 'Primary Insured Date of Birth',
  },
  {
    id: 'insuranceTertiary.primaryInsuredSex',
    source: 'insuranceTertiary',
    label: 'Primary Insured Sex',
  },

  // Diagnosis (staff selects one or more at generation time)
  { id: 'diagnosis.codes', source: 'diagnosis', label: 'Code(s)' },
  { id: 'diagnosis.descriptions', source: 'diagnosis', label: 'Description(s)' },
  { id: 'diagnosis.codesAndDescriptions', source: 'diagnosis', label: 'Code + Description' },

  // Complaint (staff selects one or more at generation time)
  { id: 'complaint.names', source: 'complaint', label: 'Complaint(s)' },

  // Provider
  { id: 'provider.firstName', source: 'provider', label: 'First Name' },
  { id: 'provider.middleInitial', source: 'provider', label: 'Middle Initial' },
  { id: 'provider.lastName', source: 'provider', label: 'Last Name' },
  { id: 'provider.suffix', source: 'provider', label: 'Suffix' },
  { id: 'provider.credentials', source: 'provider', label: 'Credentials' },
  { id: 'provider.signature', source: 'provider', label: 'Signature' },
  { id: 'provider.npi', source: 'provider', label: 'NPI Number' },
  { id: 'provider.taxonomyCode', source: 'provider', label: 'Taxonomy Code' },
  { id: 'provider.taxId', source: 'provider', label: 'Tax ID' },
  { id: 'provider.licenseType', source: 'provider', label: 'License Type' },
  { id: 'provider.licenseNumber', source: 'provider', label: 'License Number' },

  // Clinic Info
  { id: 'clinic.name', source: 'clinic', label: 'Name' },
  { id: 'clinic.address1', source: 'clinic', label: 'Address' },
  { id: 'clinic.address2', source: 'clinic', label: 'Address cont.' },
  { id: 'clinic.city', source: 'clinic', label: 'City' },
  { id: 'clinic.state', source: 'clinic', label: 'State' },
  { id: 'clinic.zip', source: 'clinic', label: 'Zip/Postal Code' },
  { id: 'clinic.country', source: 'clinic', label: 'Country' },
  { id: 'clinic.npi', source: 'clinic', label: 'NPI Number' },
  { id: 'clinic.phone', source: 'clinic', label: 'Phone' },
  { id: 'clinic.fax', source: 'clinic', label: 'Fax' },
  { id: 'clinic.email', source: 'clinic', label: 'Email' },
  { id: 'clinic.site', source: 'clinic', label: 'Site' },

  // Healthcare Provider (selected referring provider)
  { id: 'healthcareProvider.firstName', source: 'healthcareProvider', label: 'First Name' },
  { id: 'healthcareProvider.middleInitial', source: 'healthcareProvider', label: 'M.I.' },
  { id: 'healthcareProvider.lastName', source: 'healthcareProvider', label: 'Last Name' },
  { id: 'healthcareProvider.suffix', source: 'healthcareProvider', label: 'Suffix' },
  { id: 'healthcareProvider.title', source: 'healthcareProvider', label: 'Title' },
  { id: 'healthcareProvider.degree', source: 'healthcareProvider', label: 'Degree' },
  { id: 'healthcareProvider.specialty', source: 'healthcareProvider', label: 'Specialty' },
  { id: 'healthcareProvider.npi', source: 'healthcareProvider', label: 'NPI Number' },
  { id: 'healthcareProvider.stateLicense', source: 'healthcareProvider', label: 'State License' },
  { id: 'healthcareProvider.email', source: 'healthcareProvider', label: 'Email' },
  { id: 'healthcareProvider.address', source: 'healthcareProvider', label: 'Address' },
  { id: 'healthcareProvider.city', source: 'healthcareProvider', label: 'City' },
  { id: 'healthcareProvider.state', source: 'healthcareProvider', label: 'State/Province' },
  { id: 'healthcareProvider.zip', source: 'healthcareProvider', label: 'Zip/Post code' },
  { id: 'healthcareProvider.country', source: 'healthcareProvider', label: 'Country' },
  { id: 'healthcareProvider.phone', source: 'healthcareProvider', label: 'Phone' },
  { id: 'healthcareProvider.fax', source: 'healthcareProvider', label: 'Fax' },
  { id: 'healthcareProvider.practiceName', source: 'healthcareProvider', label: 'Practice Name' },

  // Healthcare Facility (selected facility)
  { id: 'healthcareFacility.facilityName', source: 'healthcareFacility', label: 'Facility Name' },
  { id: 'healthcareFacility.address', source: 'healthcareFacility', label: 'Address' },
  { id: 'healthcareFacility.city', source: 'healthcareFacility', label: 'City' },
  { id: 'healthcareFacility.state', source: 'healthcareFacility', label: 'State' },
  { id: 'healthcareFacility.zip', source: 'healthcareFacility', label: 'Zip/Post code' },
  { id: 'healthcareFacility.country', source: 'healthcareFacility', label: 'Country' },
  { id: 'healthcareFacility.phone', source: 'healthcareFacility', label: 'Phone' },
  { id: 'healthcareFacility.fax', source: 'healthcareFacility', label: 'Fax' },
] as const satisfies readonly FormToken[];

export type FormTokenId = (typeof FORM_TOKENS)[number]['id'];

/** Sources that require staff to pick a record at generation time (no patient-implicit value). */
export const FORM_TOKEN_SELECTION_SOURCES: FormTokenSource[] = [
  'provider',
  'healthcareProvider',
  'healthcareFacility',
];

/**
 * Sources that prompt staff to select one or more clinical items at generation
 * time (diagnoses / complaints), distinct from the single-record provider/facility
 * pickers. Selected values are resolved into the diagnosis/complaint tokens.
 */
export const FORM_DIAGNOSIS_TOKEN_SOURCES: FormTokenSource[] = ['diagnosis', 'complaint'];

/** A diagnosis the staff member selected at generation time (ICD-10 code +
 * description), resolved into the `diagnosis.*` tokens. */
export interface FormDiagnosisSelection {
  code: string;
  description?: string;
}

/** Staff selections for the diagnosis/complaint token prompt, threaded from the
 * generate/send UI → endpoint → token resolver. Optional everywhere; only the
 * forms that use these tokens collect them. */
export interface FormClinicalSelections {
  diagnoses?: FormDiagnosisSelection[];
  /** Complaint names (free text or chosen from the patient's existing complaints). */
  complaints?: string[];
}

/**
 * Token sources eligible for the per-element "Hide if Token Missing" rule — when on,
 * a token element (or text block containing such a token) is dropped from the
 * generated form if the patient has no value for it. Limited to patient demographics
 * and the three insurance ranks, where a missing value commonly leaves a blank gap.
 */
export const HIDE_IF_TOKEN_MISSING_SOURCES: FormTokenSource[] = [
  'patient',
  'insurancePrimary',
  'insuranceSecondary',
  'insuranceTertiary',
];

/* ------------------------------------------------------------------ *
 * Form elements (positioned canvas / PDF overlays)
 * ------------------------------------------------------------------ */

/**
 * Every custom-form element type — the single source of truth. The FormElementType
 * union is derived from this, and the sanitizer's valid-type set is built from it,
 * so adding a type here flows through both at once (the sanitizer can't silently
 * drop a newly-added type). Capability subsets (requireable / borderable /
 * visibility-controller) are typed against FormElementType so a stale entry errors.
 */
export const FORM_ELEMENT_TYPES = [
  'staticText',
  'singleLine',
  'multiLine',
  'token',
  'signature',
  'date',
  'checkbox',
  'radio',
  'dropdown',
  'number',
  'sectionDivider',
] as const;

export type FormElementType = (typeof FORM_ELEMENT_TYPES)[number];

/** Element types that may overlay an uploaded PDF (excludes static text + dividers). */
export const PDF_OVERLAY_ELEMENT_TYPES: FormElementType[] = [
  'singleLine',
  'multiLine',
  'token',
  'signature',
  'date',
  'checkbox',
  'radio',
  'dropdown',
  'number',
];

export type FormTextAlign = 'left' | 'center' | 'right';

export interface FormTextFormatting {
  bold?: boolean;
  italic?: boolean;
  fontSize?: number;
  align?: FormTextAlign;
}

export type FormBorderStyle = 'none' | 'solid' | 'dashed' | 'dotted' | 'underline';
export type FormBorderColor = 'black' | 'gray' | 'slate' | 'blue' | 'green' | 'red';

/** Per-field border (box or underline) for the value-bearing input fields. */
export interface FormFieldBorder {
  style: FormBorderStyle;
  color?: FormBorderColor;
  /** Stroke width in canvas px (1–4). */
  width?: number;
}

export const FORM_BORDER_COLORS: { value: FormBorderColor; label: string; hex: string }[] = [
  { value: 'black', label: 'Black', hex: '#111827' },
  { value: 'gray', label: 'Gray', hex: '#6b7280' },
  { value: 'slate', label: 'Slate', hex: '#475569' },
  { value: 'blue', label: 'Blue', hex: '#2563eb' },
  { value: 'green', label: 'Green', hex: '#16a34a' },
  { value: 'red', label: 'Red', hex: '#dc2626' },
];

/** Opaque background fill for a field — used in pdf-overlay mode to MASK the
 * underlying PDF content (pre-printed lines/text) behind a placed field. */
export type FormFillColor = 'none' | 'white' | FormBorderColor;

export const FORM_FILL_COLORS: { value: FormFillColor; label: string; hex: string | null }[] = [
  { value: 'none', label: 'None', hex: null },
  { value: 'white', label: 'White', hex: '#ffffff' },
  ...FORM_BORDER_COLORS.map(c => ({ value: c.value as FormFillColor, label: c.label, hex: c.hex })),
];

// ── Conditional visibility ───────────────────────────────────────────────────
// A field can be shown only when another field's value matches a rule, e.g.
// show "Please describe" only when "New problems?" equals "Yes". v1 = one rule.
export type FormConditionOperator = 'equals' | 'notEquals' | 'isFilled' | 'isEmpty';

export interface FormCondition {
  /** Element id of the controlling field. */
  fieldId: string;
  operator: FormConditionOperator;
  /** Compare value: an option id (radio/dropdown), 'true'/'false' (checkbox), or text. */
  value?: string;
}

/** Geometry shared by every element. Coordinates are normalized to the page box. */
export interface FormElementBase {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  page: number;
  label?: string;
  required?: boolean;
  /** Optional visible border for value-bearing input fields. */
  border?: FormFieldBorder;
  /** Optional opaque background fill — masks underlying PDF content in overlay mode. */
  fill?: FormFillColor;
  /** When set, this field is shown only while the condition holds. */
  visibleWhen?: FormCondition;
  /**
   * Auto-Populate Token elements and Text Blocks containing ≥1 token only: when
   * true, the element is dropped from the generated form if its token value(s)
   * don't exist for that patient. Only honored for tokens from
   * HIDE_IF_TOKEN_MISSING_SOURCES (patient demographics / insurance ranks).
   */
  hideIfTokenMissing?: boolean;
  /**
   * Who completes this field: 'patient' (default) or 'user' (clinic staff). A form
   * with any user-owned field can't be sent through Request Info — it must be
   * generated in-app from the patient chart so staff fill their fields first. Absent
   * = 'patient'. Not shown in the builder when the field auto-populates from a token.
   */
  completedBy?: 'patient' | 'user';
}

export interface StaticTextElement extends FormElementBase {
  type: 'staticText';
  text: string;
  formatting?: FormTextFormatting;
}

export interface SingleLineElement extends FormElementBase {
  type: 'singleLine';
  placeholder?: string;
  value?: string;
}

export interface MultiLineElement extends FormElementBase {
  type: 'multiLine';
  placeholder?: string;
  value?: string;
}

export interface TokenElement extends FormElementBase {
  type: 'token';
  tokenId: FormTokenId;
  value?: string;
}

export interface SignatureElement extends FormElementBase {
  type: 'signature';
}

export interface DateElement extends FormElementBase {
  type: 'date';
  /** When true, resolves to today's date at generation time; otherwise left blank. */
  autoToday?: boolean;
  value?: string;
}

export interface CheckboxElement extends FormElementBase {
  type: 'checkbox';
  checked?: boolean;
}

export interface RadioOption {
  id: string;
  label: string;
}

export interface RadioElement extends FormElementBase {
  type: 'radio';
  options: RadioOption[];
  selectedOptionId?: string;
}

/** A single-select dropdown. Same option shape as radio; value is the option id. */
export interface DropdownElement extends FormElementBase {
  type: 'dropdown';
  options: RadioOption[];
  selectedOptionId?: string;
}

/** Numeric input; when `currency` is set the value is displayed as USD ($, 2dp). */
export interface NumberElement extends FormElementBase {
  type: 'number';
  currency?: boolean;
  placeholder?: string;
  value?: string;
}

export interface SectionDividerElement extends FormElementBase {
  type: 'sectionDivider';
}

export type CustomFormElement =
  | StaticTextElement
  | SingleLineElement
  | MultiLineElement
  | TokenElement
  | SignatureElement
  | DateElement
  | CheckboxElement
  | RadioElement
  | DropdownElement
  | NumberElement
  | SectionDividerElement;

export interface FormPageSize {
  width: number;
  height: number;
}

/* ------------------------------------------------------------------ *
 * Template + instance
 * ------------------------------------------------------------------ */

export interface CustomForm {
  id: string;
  clinicId: number;
  name: string;
  displayName?: string;
  subtitle?: string;
  category: FormCategory;
  mode: FormRenderMode;
  /** Set only for `pdf-overlay` mode. */
  basePdfS3Key?: string | null;
  pageSize?: FormPageSize | null;
  pageCount?: number | null;
  elements: CustomFormElement[];
  sendToPatient: boolean;
  /** Global folder id, or null = system-default "Forms" folder. */
  destinationFolderId?: number | null;
  isPublishedToLibrary?: boolean;
  libraryDescription?: string | null;
  showClinicName?: boolean;
  createdBy?: string;
  createdByName?: string;
  updatedBy?: string;
  createdAt?: number;
  updatedAt?: number;
  deletedAt?: number | null;
}

/** List-endpoint row: a CustomForm minus the heavy `elements` array, plus a
 * precomputed `elementCount`. Threaded through list endpoint → service → list page
 * so no consumer has to cast to read `elementCount`. */
export type CustomFormListItem = Omit<CustomForm, 'elements'> & {
  elementCount: number;
  /** True when any element is user-owned (completedBy === 'user'). Such forms are
   * excluded from Request Info. Computed by the list endpoint from the elements. */
  hasUserOwnedField?: boolean;
};

export type CustomFormInstanceStatus = 'draft' | 'sent' | 'completed' | 'printed' | 'saved';

export interface CustomFormInstance {
  id: string;
  clinicId: number;
  patientId: string;
  formId: string;
  encounterId?: string | null;
  providerId?: number | null;
  facilityId?: number | null;
  /** Resolved-then-staff-edited values keyed by element id. */
  fieldValues: Record<string, string>;
  /** Tokens that could not be resolved at generation time (rendered blank). */
  unresolvedTokens: FormTokenId[];
  status: CustomFormInstanceStatus;
  /** Set once the rendered PDF is stored in the document tab. */
  documentId?: number | null;
  generatedBy?: string;
  generatedByName?: string;
  signedAt?: number | null;
  createdAt?: number;
  updatedAt?: number;
  deletedAt?: number | null;
}

export interface FormLibraryItem {
  id: string;
  name: string;
  displayName?: string;
  subtitle?: string;
  category: FormCategory;
  libraryDescription?: string;
  elementCount: number;
  importCount?: number;
  publishedByClinicName?: string;
  createdAt?: number;
  updatedAt?: number;
}

export type FormLibrarySortOption = 'name' | 'popular' | 'recent';

export const defaultCustomForm: Omit<CustomForm, 'clinicId' | 'createdBy'> = {
  id: '',
  name: '',
  displayName: '',
  subtitle: '',
  category: 'clinical',
  mode: 'scratch',
  elements: [],
  sendToPatient: false,
  destinationFolderId: null,
};
